import { useMemo } from "react";
import { NativeEventEmitter, type EmitterSubscription } from "react-native";

import { useBridge } from "../BridgeContext";
import type { BroadcastModule } from "../internal/NativeModulesTypes";
import type {
  BroadcastEvent,
  BroadcastEventMap,
  BroadcastEventName,
  JSTBroadcastSubscription,
} from "./JSTBroadcast.types";

export type * from "./JSTBroadcast.types";

export function createJSTBroadcast(getBridge: () => BroadcastModule | undefined) {
  let eventEmitter: NativeEventEmitter | null = null;
  return {
    async subscribe<T extends BroadcastEventName>(
      eventType: T,
      callback: (event: BroadcastEventMap[T]) => void,
    ): Promise<JSTBroadcastSubscription | null> {
      const nativeModule = getBridge();
      if (!nativeModule?.subscribe) return null;
      eventEmitter ??= new NativeEventEmitter(nativeModule);
      const listener: EmitterSubscription = eventEmitter.addListener(eventType, callback);
      let handle: string;
      try {
        handle = await nativeModule.subscribe(eventType);
      } catch (error) {
        console.error(`Failed to subscribe to JSTBroadcast for event type: ${eventType}`, error);
        listener.remove();
        return null;
      }
      return {
        remove: async () => {
          listener.remove();
          try {
            await nativeModule.unsubscribe?.(handle);
          } catch (error) {
            console.error("Failed to unsubscribe from JSTBroadcast", error);
          }
        },
      };
    },
    async dispatch(event: BroadcastEvent): Promise<void> {
      const nativeModule = getBridge();
      await nativeModule?.dispatch?.(event);
    },
  };
}

export function useJSTBroadcast() {
  const broadcast = useBridge().RNBroadcastModule;
  return useMemo(() => createJSTBroadcast(() => broadcast), [broadcast]);
}

export default useJSTBroadcast;
